import {defineStore} from 'pinia'
import {ref, shallowRef, computed, watch} from 'vue'
import {useStorage} from '@vueuse/core'
import getRequestPartsFromUrl from 'shared/utils/getRequestPartsFromUrl'
import getGraphQLOperationName from 'shared/utils/getGraphQLOperationName'
import sortArrayByObjectProperty from 'shared/utils/sortArrayByObjectProperty'
import {useSessionNetworkRequestsFilter} from 'shared/composables/useSessionNetworkRequestsFilter'
import {useRouteQuery} from 'shared/composables/useRouteQuery'
import enrichRequestV3 from 'shared/utils/enrichRequestV3'
import enrichRequest from 'shared/utils/enrichRequest'
import {useSessionStore} from 'shared/stores/session'

export const useSessionNetworkStore = defineStore('sessionNetwork', () => {
	const sessionStore = useSessionStore()

	const rawNetworkRequests = shallowRef()
	const requestsByDataSource = ref()

	/*****************************
	 *  PROCESSING NETWORK REQUESTS
	 *  only happens when the session network pane is shown, and the function translateNetworkDataSources() is called
	 */

	function processRawNetworkRequests() {
		if (!rawNetworkRequests.value) return undefined
		/**
		 * Due to a bad decision when rolling out network request recording, the network JSON contains two properties:
		 * - `requests`: Refers to requests recorded from the extension `WebRequest` in `network.js`;
		 * - `responses`: Refers to requests recorded from `fetch` and `XMLHttpRequest` in `instrumentNetwork.js`;
		 * This computed prop translates our persisted data back, so that it's explicit about the data sources.
		 */
		const fromExtensionAPI =
			rawNetworkRequests.value?.fromExtensionAPI ||
			rawNetworkRequests.value?.requests ||
			(Array.isArray(rawNetworkRequests.value) && rawNetworkRequests.value) ||
			null
		const fromInstrumentation =
			rawNetworkRequests.value?.fromInstrumentation || rawNetworkRequests.value?.responses || []
		const version = rawNetworkRequests.value?.version
		const requests = {fromExtensionAPI, fromInstrumentation, version}
		if (fromExtensionAPI) {
			requests.fromExtensionAPI = addExtraPropertiesToRequests(fromExtensionAPI)
		}
		// There can be no requests `fromExtensionAPI`, but from instrumentation, because SDK only records the latter
		// Or with "version 2", the browser extension records XHR and `fetch` requests only through instrumentation.
		// In both cases, requests `fromInstrumentation` end up being displayed in the request list and therefore need the additional properties.
		if (!fromExtensionAPI || version >= 2) {
			requests.fromInstrumentation = addExtraPropertiesToRequests(fromInstrumentation)
		}
		requestsByDataSource.value = requests
	}

	function addExtraPropertiesToRequests(requests) {
		return requests.map(request => {
			try {
				// Don't mutate the request, as this would affect the uploaded network data.
				request = {...request}
				// Normalize request ID to string, which it started as for historical reasons.
				request.id = String(request.id)

				const {name, path, protocol, domain} = getRequestPartsFromUrl(request.url)
				request.path = path
				request.protocol = protocol
				request.name = name
				request.domain = domain
				request.method = request.method?.toUpperCase()
				const gqlOperationName = getGraphQLOperationName(request)
				if (gqlOperationName) {
					request.gqlOperationName = gqlOperationName
				}
				if (request.startedAt) {
					request.millisecondsSinceSessionStart = sessionStore.calculateMillisecondsSinceSessionStart(
						request.startedAt
					)
				}
			} catch (error) {
				// Don't break the whole session list when processing of extra properties fails for 1 singular request.

				// eslint-disable-next-line no-console
				console.error(error)
			}
			return request
		})
	}

	const version = computed(() => requestsByDataSource.value?.version)
	const fromExtensionAPI = computed(() => requestsByDataSource.value?.fromExtensionAPI)
	const fromInstrumentation = computed(() => requestsByDataSource.value?.fromInstrumentation || [])
	const enrichRequestFunction = computed(() => (version.value >= 3 ? enrichRequestV3 : enrichRequest))

	const processedRequests = computed(() => {
		if (!requestsByDataSource.value) return

		const hasExtensionAPIRequests = !!fromExtensionAPI.value
		const hasInstrumentationRequests = fromInstrumentation.value.length > 0
		const hasRequestsFromBothSources = hasExtensionAPIRequests && hasInstrumentationRequests

		if (hasRequestsFromBothSources && version.value >= 3) {
			// On V3+, use instrumentation as the source of truth for XMLHttpRequests and fetch requests,
			// both of type xmlhttprequest, and combine with extension API for document requests to display in dev tools
			const documentRequests = fromExtensionAPI.value.filter(request => request.type !== 'xmlhttprequest')
			return mergeAndSortRequests(documentRequests, fromInstrumentation.value)
		}
		if (hasRequestsFromBothSources && version.value === 2) {
			// On V2, the browser extension recorded all XMLHttpRequest and fetch with instrumentation,
			// and all document triggered requests with the extension API
			return mergeAndSortRequests(fromExtensionAPI.value, fromInstrumentation.value)
		}
		if (hasExtensionAPIRequests) {
			// For versions up to V1.63.0, only extension API requests are available;
			// this logic also applies as a fallback for future versions, ensuring backward compatibility
			// and handling cases where only extension API requests are available
			return fromExtensionAPI.value
		}
		// Default to instrumentation requests if no extension API requests are present,
		// such as in the SDK, which records just with instrumentation
		return fromInstrumentation.value
	})

	function mergeAndSortRequests(fromExtensionAPI, fromInstrumentation) {
		return sortArrayByObjectProperty([...fromExtensionAPI, ...fromInstrumentation], 'startedAt')
	}

	/*****************************
	 *  NETWORK REQUESTS TABLE
	 */
	const availableColumns = useStorage('network-requests-table-columns', [
		{
			name: 'Start',
			key: 'start',
			visible: true,
			width: '64px',
			fixed: true,
		},
		{
			name: 'Name',
			key: 'name',
			visible: true,
			width: '1fr',
			fixed: true,
			defaultWidth: '1fr',
		},
		{
			name: 'Path',
			key: 'path',
			visible: false,
			width: '1fr',
			defaultVisible: false,
			defaultWidth: '1fr',
		},
		{
			name: 'Url',
			key: 'url',
			visible: false,
			width: '1fr',
			defaultVisible: false,
			defaultWidth: '1fr',
		},
		{
			name: 'Status',
			key: 'status',
			visible: true,
			width: '70px',
			defaultVisible: true,
			defaultWidth: '70px',
		},
		{
			name: 'Method',
			key: 'method',
			visible: true,
			width: '70px',
			defaultVisible: true,
			defaultWidth: '70px',
		},
		{
			name: 'Protocol',
			key: 'protocol',
			visible: false,
			width: '70px',
			defaultVisible: false,
			defaultWidth: '70px',
		},
		{
			name: 'Domain',
			key: 'domain',
			visible: false,
			width: '1fr',
			defaultVisible: false,
			defaultWidth: '1fr',
		},
		{
			name: 'Initiator',
			key: 'initiator',
			visible: false,
			width: '1fr',
			defaultVisible: false,
			defaultWidth: '1fr',
		},
	])

	const fixedColumns = computed(() => availableColumns.value.filter(column => column.fixed))

	const columnMinWidth = 50

	const displayedColumns = computed(() => {
		if (isSpecificRequestSelected.value) {
			return fixedColumns.value
		}
		return availableColumns.value.filter(column => column.visible)
	})

	const gridTemplateColumns = computed(() => {
		return displayedColumns.value?.map(column => column.width).join(' ') || ''
	})

	function toggleColumnVisibility(key) {
		const column = availableColumns.value.find(column => column.key === key && !column.fixed)
		if (!column) return
		column.visible = !column.visible
	}

	function updateColumnWidths(keyWidthPairs) {
		keyWidthPairs.forEach(([key, width]) => {
			if (width < columnMinWidth) return
			const column = availableColumns.value.find(column => {
				return column.key === key
			})
			if (!column) return
			column.width = `${width}px`
		})
		// ensure the fixed columns are always reset back to their default width
		// which should still keep its new resized value in case their default width was 1fr
		fixedColumns.value.forEach(column => {
			column.width = column.defaultWidth || column.width
		})
	}

	function resetColumns() {
		availableColumns.value.forEach(column => {
			column.visible = column.defaultVisible || column.fixed
			column.width = column.defaultWidth || column.width
		})
	}

	/*****************************
	 *  FILTERING
	 */
	const {
		requestTypeFilters,
		filterOptions,
		displayedRequests,
		isAnyCollapsibleFilterActive,
		setRequestTypeFilter,
		resetFilters,
	} = useSessionNetworkRequestsFilter({processedRequests, displayedColumns})

	/*****************************
	 *  SELECTING REQUEST
	 */
	const selectedRequestId = useRouteQuery('requestId')

	const selectedRequest = computed(() => {
		if (isNaN(selectedRequestId.value)) return
		const request = displayedRequests.value.find(request => request.id === selectedRequestId.value)
		return request
	})
	const isSpecificRequestSelected = computed(() => !!selectedRequest.value)

	/*****************************
	 *  ENRICHING SELECTED REQUEST
	 */
	// create a weak map that will have a selected request as key and a matching request as value
	const enrichedRequestsMap = new WeakMap()
	const enrichedRequest = ref()

	watch(
		selectedRequest,
		request => {
			if (!request) {
				enrichedRequest.value = null
				return
			}
			if (enrichedRequestsMap.has(request)) {
				enrichedRequest.value = enrichedRequestsMap.get(request)
				return
			}
			enrichedRequest.value = enrichRequestFunction.value(
				request,
				fromExtensionAPI.value,
				fromInstrumentation.value
			)
			enrichedRequestsMap.set(request, enrichedRequest.value)
		},
		{immediate: true}
	)

	return {
		// pre-processing
		rawNetworkRequests,
		processRawNetworkRequests,
		//setup
		processedRequests,
		fromExtensionAPI,
		fromInstrumentation,
		// filtering
		requestTypeFilters,
		filterOptions,
		isAnyCollapsibleFilterActive,
		displayedRequests,
		setRequestTypeFilter,
		resetFilters,
		// table
		availableColumns,
		displayedColumns,
		gridTemplateColumns,
		columnMinWidth,
		toggleColumnVisibility,
		updateColumnWidths,
		resetColumns,
		// selecting
		selectedRequestId,
		selectedRequest,
		isSpecificRequestSelected,
		enrichedRequest,
	}
})
